// hooks/useAppModals.ts
import { useState, useCallback, type Dispatch, type SetStateAction } from 'react';
import { userService } from '../services/userService';
import type { User } from '@supabase/supabase-js';
import type { DbUser } from '../contexts/AppContext';
import type { PlanOperations } from './useAcademicPlan';
import type { ModalOps } from './useModals';
import type { CourseInput } from './academicPlan/useCourseOperations';
import type { AddCoursesItem, AddCoursesResult } from '../services/typedApiClient';

// =============================================================================
// TYPES
// =============================================================================

// Re-exported so existing imports of `useAppModals.DbUser` keep working.
// The canonical source is AppContext.
export type { DbUser } from '../contexts/AppContext';

/**
 * Toast handler functions
 */
export interface ToastHandlers {
  showSuccess: (message: string, options?: Record<string, unknown>) => void;
  showWarning: (message: string, options?: Record<string, unknown>) => void;
  showError: (message: string, options?: Record<string, unknown>) => void;
}

/**
 * Parameters for the useAppModals hook.
 *
 * Note: `planOps` and `modalOps` use the canonical types from useAcademicPlan
 * and useModals respectively (rather than narrower local re-declarations).
 */
export interface UseAppModalsParams {
  user: User | null;
  dbUser: DbUser | null;
  setDbUser: Dispatch<SetStateAction<DbUser | null>>;
  setSyncingUser: Dispatch<SetStateAction<boolean>>;
  planOps: PlanOperations | null;
  modalOps: ModalOps;
  showSuccess: ToastHandlers['showSuccess'];
  showWarning: ToastHandlers['showWarning'];
  showError: ToastHandlers['showError'];
}

/**
 * Result from AP Credits save operation
 */
export interface APCreditsResult {
  total_credits: number;
  [key: string]: unknown;
}

/**
 * Result from timeline save operation
 */
export interface TimelineResult {
  cleared?: boolean;
  semesterCount?: number | string;
  [key: string]: unknown;
}

/**
 * Return type for the useAppModals hook
 */
export interface UseAppModalsReturn {
  // AP Credits Modal
  isAPCreditsModalOpen: boolean;
  handleOpenAPCredits: () => void;
  handleCloseAPCredits: () => void;
  handleAPCreditsSave: (result: APCreditsResult) => Promise<void>;

  // Timeline Modal
  isTimelineModalOpen: boolean;
  handleSetupTimeline: () => void;
  handleCloseTimelineModal: () => void;
  handleTimelineSave: (result: TimelineResult) => Promise<void>;

  // Program Selection
  handleSelectPrograms: () => void;
  handleProgramsUpdated: () => Promise<void>;

  // Semester Management
  showSemesterModal: boolean;
  setShowSemesterModal: (show: boolean) => void;
  handleManageSemesters: () => void;

  // Other Credit Modal
  isOtherCreditModalOpen: boolean;
  handleOpenOtherCredit: () => void;
  handleCloseOtherCredit: () => void;
  handleOtherCreditSubmit: (creditData: Record<string, unknown>) => Promise<void>;

  // Add Course Modal
  isAddCourseModalOpen: boolean;
  addCourseTargetSemester: { code: string; name: string } | null;
  handleOpenAddCourse: (semesterCode: string, semesterName: string) => void;
  handleCloseAddCourse: () => void;
  handleAddCourseFromModal: (course: CourseInput, semesterCode: string) => Promise<void>;
  handleAddCoursesFromModal: (items: AddCoursesItem[], semesterCode: string) => Promise<AddCoursesResult | null>;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to manage application-level modal state and handlers.
 * Extracts modal logic from App.js for AP Credits, Timeline, and Program Selection.
 */
export function useAppModals({
  user,
  dbUser,
  setDbUser,
  setSyncingUser,
  planOps,
  modalOps,
  showSuccess,
  showWarning,
  showError
}: UseAppModalsParams): UseAppModalsReturn {
  // AP Credits Modal state
  const [isAPCreditsModalOpen, setIsAPCreditsModalOpen] = useState(false);

  // Timeline Setup Modal state
  const [isTimelineModalOpen, setIsTimelineModalOpen] = useState(false);

  // Semester Management Modal state
  const [showSemesterModal, setShowSemesterModal] = useState(false);

  // AP Credits Modal handlers
  const handleOpenAPCredits = useCallback(() => {
    setIsAPCreditsModalOpen(true);
  }, []);

  const handleCloseAPCredits = useCallback(() => {
    setIsAPCreditsModalOpen(false);
  }, []);

  const handleAPCreditsSave = useCallback(async (result: APCreditsResult): Promise<void> => {
    if (result.total_credits > 0) {
      showSuccess(
        `${result.total_credits} AP credits applied to your academic plan!`,
        {
          title: 'AP Credits Updated',
          subMessage: 'Check the AP Credits section to see your new courses.',
          duration: 6000
        }
      );
    }
  }, [showSuccess]);

  // Timeline Setup Modal handlers
  const handleSetupTimeline = useCallback(() => {
    setIsTimelineModalOpen(true);
  }, []);

  const handleCloseTimelineModal = useCallback(() => {
    setIsTimelineModalOpen(false);
  }, []);

  const handleTimelineSave = useCallback(async (result: TimelineResult): Promise<void> => {
    setIsTimelineModalOpen(false);

    try {
      if (result.cleared) {
        showSuccess(
          'Timeline cleared successfully! Your empty state will now appear.',
          {
            title: 'Timeline Reset',
            subMessage: 'You can set up your timeline again anytime.',
            duration: 4000
          }
        );
      } else {
        const semesterCount = result.semesterCount || 'several';
        showSuccess(
          `Your ${semesterCount} semester timeline is ready! Start adding courses to your plan.`,
          {
            title: 'Academic Plan Generated',
            subMessage: 'Your semesters have been created based on your enrollment dates.',
            duration: 5000
          }
        );
      }

      // Force refresh of user data to update the empty state
      if (user) {
        setSyncingUser(true);
        try {
          const response = await userService.syncUser(user);
          // Only set dbUser if response contains valid user data (has an id)
          if (response && response.id) {
            // SyncUserResponse is assignable to DbUser since DbUser extends it
            setDbUser(response as unknown as DbUser);
            localStorage.setItem('academic_planner_db_user', JSON.stringify(response));
          }
        } catch (error) {
          console.error('Failed to refresh user data:', error);
          showError('Timeline operation completed, but failed to refresh profile. Please refresh the page.');
          return;
        } finally {
          setSyncingUser(false);
        }

        // Refresh the academic plan
        if (planOps && planOps.refreshPlan) {
          await planOps.refreshPlan();
        } else {
          console.error('refreshPlan not available, reloading page...');
          showWarning('Timeline operation completed! Please refresh the page to see changes.');
        }
      }
    } catch (error) {
      console.error('Error in timeline operation:', error);
      showError('Timeline operation completed, but there was an issue updating your plan. Please refresh the page.');
    }
  }, [showSuccess, showError, showWarning, user, setSyncingUser, setDbUser, planOps]);

  // Program Selection Handler
  const handleSelectPrograms = useCallback(() => {
    modalOps.openProgramSelection();
  }, [modalOps]);

  // Program Selection Update Handler
  const handleProgramsUpdated = useCallback(async (): Promise<void> => {
    try {
      if (planOps && planOps.refreshPlan) {
        await planOps.refreshPlan();
      }

      showSuccess(
        'Programs updated successfully! Your degree requirements are now available.',
        {
          title: 'Programs Selected',
          subMessage: 'Check the Degree Audit to see your new requirements.',
          duration: 5000
        }
      );
    } catch (error) {
      console.error('Error updating programs:', error);
      showError('Programs updated, but there was an issue refreshing your plan. Please refresh the page.');
    }
  }, [planOps, showSuccess, showError]);

  // Semester Management Handler
  const handleManageSemesters = useCallback(() => {
    setShowSemesterModal(true);
  }, []);

  // Other Credit Modal state
  const [isOtherCreditModalOpen, setIsOtherCreditModalOpen] = useState(false);

  const handleOpenOtherCredit = useCallback(() => {
    setIsOtherCreditModalOpen(true);
  }, []);

  const handleCloseOtherCredit = useCallback(() => {
    setIsOtherCreditModalOpen(false);
  }, []);

  const handleOtherCreditSubmit = useCallback(async (creditData: Record<string, unknown>): Promise<void> => {
    const courseCode = String(creditData.course_code ?? creditData.title ?? '');
    const courseToAdd = {
      code: courseCode,
      title: creditData.title as string | undefined,
      credits: typeof creditData.credits === 'number' ? creditData.credits : 3,
      grade: (creditData.grade as string | null | undefined) ?? null,
      source_type: (creditData.source_type as string | undefined) ?? 'other_credits',
      institution: (creditData.institution as string | null | undefined) ?? null,
      notes: (creditData.notes as string | null | undefined) ?? null,
      isPlaceholder: creditData.type === 'placeholder',
    };

    await planOps?.addCourse?.(courseToAdd, '000009');
    showSuccess('Credit added successfully');
    setIsOtherCreditModalOpen(false);
  }, [planOps, showSuccess]);

  // Add Course Modal state
  const [isAddCourseModalOpen, setIsAddCourseModalOpen] = useState(false);
  const [addCourseTargetSemester, setAddCourseTargetSemester] = useState<{ code: string; name: string } | null>(null);

  const handleOpenAddCourse = useCallback((semesterCode: string, semesterName: string) => {
    setAddCourseTargetSemester({ code: semesterCode, name: semesterName });
    setIsAddCourseModalOpen(true);
  }, []);

  const handleCloseAddCourse = useCallback(() => {
    setIsAddCourseModalOpen(false);
  }, []);

  const handleAddCourseFromModal = useCallback(async (course: CourseInput, semesterCode: string): Promise<void> => {
    await planOps?.addCourse?.(course, semesterCode);
    showSuccess(`Added ${course.code || course.title} to ${addCourseTargetSemester?.name || 'semester'}`);
    setIsAddCourseModalOpen(false);
  }, [planOps, showSuccess, addCourseTargetSemester]);

  // Multi-select add. Toasts the courses that went in (and any warnings the
  // backend raised about them, e.g. already covered by AP credit) and returns
  // the result: the modal closes when everything was added and otherwise
  // keeps the failed courses listed with their reasons.
  const handleAddCoursesFromModal = useCallback(async (
    items: AddCoursesItem[],
    semesterCode: string,
  ): Promise<AddCoursesResult | null> => {
    const result = await planOps?.addCourses?.(items, semesterCode);
    if (!result) return null;

    const semesterName = addCourseTargetSemester?.name || 'semester';
    const added = result.results.filter((r) => r.status === 'added');
    if (added.length === 1) {
      showSuccess(`Added ${added[0].course_code} to ${semesterName}`);
    } else if (added.length > 1) {
      showSuccess(`Added ${added.length} courses to ${semesterName}`);
    }
    for (const entry of added) {
      for (const warning of entry.warnings) {
        showWarning(warning, { duration: 8000 });
      }
    }
    return result;
  }, [planOps, showSuccess, showWarning, addCourseTargetSemester]);

  return {
    // AP Credits Modal
    isAPCreditsModalOpen,
    handleOpenAPCredits,
    handleCloseAPCredits,
    handleAPCreditsSave,

    // Timeline Modal
    isTimelineModalOpen,
    handleSetupTimeline,
    handleCloseTimelineModal,
    handleTimelineSave,

    // Program Selection
    handleSelectPrograms,
    handleProgramsUpdated,

    // Semester Management
    showSemesterModal,
    setShowSemesterModal,
    handleManageSemesters,

    // Other Credit Modal
    isOtherCreditModalOpen,
    handleOpenOtherCredit,
    handleCloseOtherCredit,
    handleOtherCreditSubmit,

    // Add Course Modal
    isAddCourseModalOpen,
    addCourseTargetSemester,
    handleOpenAddCourse,
    handleCloseAddCourse,
    handleAddCourseFromModal,
    handleAddCoursesFromModal
  };
}
